import { h } from "@stencil/core";
import { JsonParser, mergeClassNames, getTranslation, hasSlot, ENTER_KEY_CODE, ESCAPE_KEY_CODE } from "../../utils";
import { aceHeaderParts, getAceHeaderNames } from "./ace-header.get-names";
const DEFAULT_TRANSLATIONS = {
    open_off_canvas_menu_button: 'Menü öffnen',
    help_center_button: 'Hilfe',
    profile_button: 'Profil ansehen',
    balance_button: 'Kontostand',
    register_button: 'Registrieren',
    login_button: 'Login',
    search_label: 'Suche',
    search_input_placeholder: 'Hier suchen...',
    toggle_search_button: 'Suchfeld öffnen oder schließen',
    logo_button: 'win2day Logo',
};
// Default win2day config
const DEFAULT_CONFIG = {
    search_button: {
        tag: 'ace-toggle-button',
        size: 'regular',
        type: 'hidden',
    },
    help_button: {
        size: 'small',
        type: 'hidden',
    },
    login_button: {
        size: 'small',
        type: 'primary',
    },
    register_button: {
        size: 'small',
        type: 'secondary',
    },
    deposit_button: {
        size: 'small',
        type: 'primary',
    },
    menu_button: {
        size: 'small',
        type: 'hidden',
    },
    profile_button: {
        size: 'small',
        type: 'hidden',
    },
};
/*
    The ace-header component provides base functionality and styling that is extended by the w2d-header and tipp3-header components.
    As it is used only under the hood, there is no documentation for it.
    To check the actual implementation, please refer to the w2d-header and tipp3-header components.
*/
export class AceHeader {
    constructor() {
        this.searchOpen = false;
        this.offcanvasMenuOpen = false;
        this.sportsFilterOpen = false;
        this.hasLogo = false;
        this.hasOffcanvasMenu = false;
        this.navContainerHeight = 0;
        this.renderMenu = () => {
            return (h("div", { class: mergeClassNames(this.names.CLASS.MENU, { [this.names.CLASS.HIDDEN_ON_SEARCH_OPEN]: this.searchOpen }) }, h("slot", { name: 'menu' })));
        };
    }
    componentWillLoad() {
        var _a;
        this.names = getAceHeaderNames((_a = this.hostElement) === null || _a === void 0 ? void 0 : _a.id);
        this.parseUserInfo();
        this.parseTranslations();
        this.parseConfig();
        this.hasLogo = hasSlot(this.hostElement, 'logo');
        this.hasOffcanvasMenu = hasSlot(this.hostElement, 'offcanvas');
    }
    componentDidLoad() {
        this.initHeightObserver();
    }
    disconnectedCallback() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }
    }
    parseUserInfo() {
        this._userInfo = new JsonParser(this.userInfo, {}).getValue();
    }
    parseTranslations() {
        this._translations = new JsonParser(this.translations, {}).getValue();
    }
    parseConfig() {
        const parsedConfig = new JsonParser(this.config, {}).getValue();
        // Update the default config with the parsed config.
        // When a field is not set in the config, it will be set to the default value.
        this._config = Object.assign(Object.assign({}, DEFAULT_CONFIG), parsedConfig);
    }
    handleBalanceButtonClicked() {
        this.balanceButtonClicked.emit();
    }
    handleRegisterButtonClicked() {
        this.registerButtonClicked.emit();
    }
    handleLoginButtonClicked() {
        this.loginButtonClicked.emit();
    }
    handleSearchInputChanged(event) {
        this.searchTerms = event.target.value;
        this.searchTermsChanged.emit(this.searchTerms);
    }
    handleSearchKeyPressed(event) {
        const keyPressed = event.key;
        if (keyPressed === ENTER_KEY_CODE) {
            event.preventDefault();
            this.searchEnterPressed.emit(this.searchTerms);
        }
    }
    handleSearchFocusOut() {
        this.searchFocusOut.emit(this.searchTerms);
    }
    showSearch() {
        this.searchOpen = true;
        this.searchToggleButtonEl.blur();
        // Manually focus on the input. This is necessary because the input is not visible when the search is opened.
        setTimeout(() => {
            var _a, _b;
            (_a = this.mobileSearchInputEl) === null || _a === void 0 ? void 0 : _a.focus();
            (_b = this.desktopSearchInputEl) === null || _b === void 0 ? void 0 : _b.focus();
        }, 200);
        this.searchOpened.emit();
    }
    hideSearch() {
        this.searchOpen = false;
        this.searchClosed.emit();
    }
    toggleSearch() {
        if (this.searchOpen) {
            this.hideSearch();
        }
        else {
            this.showSearch();
        }
    }
    showOffcanvasMenu() {
        this.offcanvasMenuOpen = true;
        this.offcanvasMenuOpened.emit();
    }
    closeOffcanvasMenu() {
        this.offcanvasMenuOpen = false;
        this.offcanvasMenuClosed.emit();
    }
    // React to clicks on the overlay from ace-header-search-results.
    handleSearchOverlayClicked() {
        this.hideSearch();
    }
    // React to clicks on the overlay to close the offcanvas menu.
    overlayClickedHandler() {
        this.closeOffcanvasMenu();
    }
    handleKeyDown(e) {
        if (e.key === ESCAPE_KEY_CODE) {
            this.offcanvasMenuOpen && this.closeOffcanvasMenu();
            this.searchOpen && this.hideSearch();
        }
    }
    // When the sports filter is open, we need to add a class to the header so that it is above the overlay of the sports filter.
    handleSportsFilterOpened() {
        this.sportsFilterOpen = true;
        this.searchOpen && this.hideSearch();
    }
    handleSportsFilterClosed() {
        this.sportsFilterOpen = false;
    }
    // React to the offcanvas menu being closed from within ace-header-offcanvas.
    // As the ace-header-offcanvas component is already emitting the "offcanvasMenuClosed" event, we do not need to emit it again.
    offcanvasMenuClosedHandler() {
        this.offcanvasMenuOpen = false;
    }
    // The height observer is used by ace-betting-section-container and ace-betting-section to update the top offset of the sticky containers. This is necessary because the height of the header can change when the notifications slot changes.
    initHeightObserver() {
        if (!this.navContainerEl)
            return;
        this.resizeObserver = new ResizeObserver(entries => {
            requestAnimationFrame(() => {
                const { contentRect } = entries[0];
                const newHeight = contentRect.height;
                if (this.navContainerHeight !== newHeight) {
                    this.navContainerHeight = newHeight;
                    this.headerHeightChanged.emit(newHeight);
                }
            });
        });
        this.resizeObserver.observe(this.navContainerEl);
    }
    renderLogo() {
        return (h("div", { class: this.names.CLASS.LOGO_WRAPPER, part: aceHeaderParts.LOGO_WRAPPER }, h("a", { href: this.logoHref ? this.logoHref : '/', "aria-label": getTranslation(this._translations, 'logo_button', DEFAULT_TRANSLATIONS), class: this.names.CLASS.LOGO, part: aceHeaderParts.LOGO, id: this.names.ID.LOGO }, h("slot", { name: 'logo' }))));
    }
    renderSearchButton() {
        const SearchButtonTag = this._config.search_button.tag;
        const additionalAttrs = Object.assign({}, (SearchButtonTag === 'ace-toggle-button' && { active: this.searchOpen }));
        return (h(SearchButtonTag, Object.assign({ size: this._config.search_button.size, "icon-name": 'search', type: this._config.search_button.type, class: this.names.CLASS.SEARCH_BUTTON, part: this.searchOpen ? aceHeaderParts.SEARCH_BUTTON_TOGGLED : aceHeaderParts.SEARCH_BUTTON, ref: el => (this.searchToggleButtonEl = el), name: getTranslation(this._translations, 'toggle_search_button', DEFAULT_TRANSLATIONS), onClick: () => this.toggleSearch(), id: this.names.ID.SEARCH_BUTTON }, additionalAttrs)));
    }
    renderHelpButton() {
        return (h("ace-button", { size: this._config.help_button.size, type: this._config.help_button.type, class: this.names.CLASS.HELP_BUTTON, "icon-name": 'help', name: getTranslation(this._translations, 'help_center_button', DEFAULT_TRANSLATIONS), href: this.helpCenterHref, id: this.names.ID.HELP_BUTTON }));
    }
    renderUserProfileButton() {
        return (h("ace-button", { "icon-name": 'profile', type: this._config.profile_button.type, href: this._userInfo.profile_href, size: this._config.profile_button.size, name: getTranslation(this._translations, 'profile_button', DEFAULT_TRANSLATIONS), color: 'text-neutral--interactive', class: this.names.CLASS.PROFILE_BUTTON, id: this.names.ID.PROFILE_BUTTON }));
    }
    renderCtas() {
        var _a;
        if ((_a = this._userInfo) === null || _a === void 0 ? void 0 : _a.balance) {
            return (h("ace-button", { type: this._config.deposit_button.type, size: this._config.deposit_button.size, "icon-name": this._config.deposit_button.iconName, class: this.names.CLASS.DEPOSIT_BUTTON, part: aceHeaderParts.DEPOSIT_BUTTON, name: getTranslation(this._translations, 'balance_button', DEFAULT_TRANSLATIONS), onClick: () => this.handleBalanceButtonClicked(), id: this.names.ID.DEPOSIT_BUTTON }, this._userInfo.balance));
        }
        else {
            return [
                h("ace-button", { type: this._config.register_button.type, size: this._config.register_button.size, "full-width": true, class: this.names.CLASS.REGISTER_BUTTON, part: aceHeaderParts.REGISTER_BUTTON, name: getTranslation(this._translations, 'register_button', DEFAULT_TRANSLATIONS), onClick: () => this.handleRegisterButtonClicked(), id: this.names.ID.REGISTER_BUTTON }, getTranslation(this._translations, 'register_button', DEFAULT_TRANSLATIONS)),
                h("ace-button", { type: this._config.login_button.type, size: this._config.login_button.size, "full-width": true, name: getTranslation(this._translations, 'login_button', DEFAULT_TRANSLATIONS), onClick: () => this.handleLoginButtonClicked(), id: this.names.ID.LOGIN_BUTTON, class: this.names.CLASS.LOGIN_BUTTON }, getTranslation(this._translations, 'login_button', DEFAULT_TRANSLATIONS)),
            ];
        }
    }
    renderOffcanvasMenuButton() {
        return (h("div", { class: this.names.CLASS.OFFCANVAS_MENU_BUTTON, part: aceHeaderParts.OFFCANVAS_MENU_BUTTON }, h("ace-button", { "icon-name": 'menu', type: this._config.menu_button.type, size: this._config.menu_button.size, name: getTranslation(this._translations, 'open_off_canvas_menu_button', DEFAULT_TRANSLATIONS), color: 'text-neutral--interactive', onClick: () => this.showOffcanvasMenu(), id: this.names.ID.OFFCANVAS_MENU_BUTTON })));
    }
    renderOffcanvasMenu() {
        const classes = {
            [this.names.CLASS.OFFCANVAS_MENU]: true,
            [this.names.CLASS.OFFCANVAS_MENU____SHOW]: this.offcanvasMenuOpen,
        };
        return (h("ace-overlay", { show: this.offcanvasMenuOpen }, h("div", { class: classes }, h("slot", { name: 'offcanvas' }))));
    }
    renderSearchInput(forMobile = false) {
        return (h("ace-input", { name: 'search-terms', "aria-label": getTranslation(this._translations, 'search_label', DEFAULT_TRANSLATIONS), "left-icon-name": forMobile ? 'search' : null, placeholder: forMobile ? getTranslation(this._translations, 'search_input_placeholder', DEFAULT_TRANSLATIONS) : null, value: this.searchTerms, onKeyUp: e => this.handleSearchKeyPressed(e), onInputFocusOut: () => this.handleSearchFocusOut(), clearable: true, autofocus: this.searchOpen, ref: el => (forMobile ? (this.mobileSearchInputEl = el) : (this.desktopSearchInputEl = el)), onInputChanged: e => this.handleSearchInputChanged(e) }));
    }
    renderMobileSearchInput() {
        return (h("div", { class: mergeClassNames(this.names.CLASS.SEARCH, this.names.CLASS.SEARCH____WITHIN_SUBMENU), part: aceHeaderParts.SEARCH_WITHIN_SUBMENU }, this.renderSearchInput(true)));
    }
    renderDesktopSearchInput() {
        return h("div", { class: mergeClassNames(this.names.CLASS.SEARCH, this.names.CLASS.SEARCH____WITHIN_NAVIGATION) }, this.renderSearchInput(false));
    }
    render() {
        var _a;
        return (h("nav", { key: '66ae4a60b13e076d2dc3196d872a21f06fc447b4', id: this.names.ID.ROOT, class: mergeClassNames(this.names.ID.ROOT), ref: el => (this.navContainerEl = el) }, h("div", { key: '0d0f0486d42ef5ba6901f40a7e62c10cf17b368c', class: this.names.CLASS.NAVBAR, part: aceHeaderParts.NAVBAR }, h("div", { key: 'c81d73f5beea8dc8cee10a63d1f766d8103091e9', class: this.names.CLASS.NAVIGATION }, this.hasLogo && this.renderLogo(), this.renderMenu()), h("div", { key: '6df6b1318784f4c80f5b1c2437015f2c54a769bc', class: this.names.CLASS.RIGHT_MENU }, h("div", { key: '5b22a9713866db56c86837540e2750c544f588e5', class: mergeClassNames(this.names.CLASS.ACTIONS_WRAPPER, this.names.CLASS.ACTIONS_WRAPPER____ICONS), part: aceHeaderParts.ACTIONS_WRAPPER_ICONS }, this.searchOpen && this.renderDesktopSearchInput(), this.renderSearchButton(), this.helpCenterHref && this.renderHelpButton(), ((_a = this._userInfo) === null || _a === void 0 ? void 0 : _a.profile_href) && this.renderUserProfileButton()), h("div", { key: '0e50fc0b3ff34a54d709fd7d8b2bd2c5b91ba33c', class: mergeClassNames(this.names.CLASS.ACTIONS_WRAPPER, this.names.CLASS.ACTIONS_WRAPPER____CTAS), part: aceHeaderParts.ACTIONS_WRAPPER_CTAS }, this.renderCtas(), this.renderOffcanvasMenuButton()))), h("div", { key: '436448bcf0b7092518ddf8fb84b08aa54d0e50ab', class: mergeClassNames(this.names.CLASS.SUBMENU, { [this.names.CLASS.HIDDEN_ON_SEARCH_OPEN]: this.searchOpen }), part: aceHeaderParts.SUBMENU }, h("slot", { key: '83aa36f64e0b62c97bf306308d9e3a8808ccb81f', name: 'submenu' })), h("div", { key: '9bf475511f2013e8d2b39ac34914ca7b489f070c', class: mergeClassNames(this.names.CLASS.NOTIFICATIONS, { [this.names.CLASS.HIDDEN_ON_SEARCH_OPEN]: this.searchOpen }) }, h("slot", { key: '91ae2b321e0f4694e4db206ead5ef38c1d0ffbda', name: 'notifications' })), this.hasOffcanvasMenu && this.renderOffcanvasMenu(), this.searchOpen && this.renderMobileSearchInput(), h("div", { key: '1bb5bd09072c88852e1c0e0669a7288c81c93379', class: mergeClassNames(this.names.CLASS.SEARCH_RESULTS, { [this.names.CLASS.SEARCH_RESULTS____VISIBLE]: this.searchOpen }) }, h("slot", { key: 'acdd7077c70539124214401ffa3a40b86c0c8923', name: 'search-results' }))));
    }
    static get is() { return "ace-header"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["./ace-header.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["ace-header.css"]
        };
    }
    static get properties() {
        return {
            "userInfo": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceHeaderUserInfo | string",
                    "resolved": "string | { balance?: string; profile_href?: string; }",
                    "references": {
                        "IAceHeaderUserInfo": {
                            "location": "import",
                            "path": "./ace-header.types",
                            "id": "src/components/ace-header/ace-header.types.ts::IAceHeaderUserInfo"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "user-info",
                "reflect": false
            },
            "logoHref": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "logo-href",
                "reflect": false
            },
            "helpCenterHref": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "help-center-href",
                "reflect": false
            },
            "searchOpen": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "search-open",
                "reflect": true,
                "defaultValue": "false"
            },
            "translations": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "IAceHeaderTranslations | string",
                    "resolved": "IAceHeaderTranslations | string",
                    "references": {
                        "IAceHeaderTranslations": {
                            "location": "import",
                            "path": "./ace-header.types",
                            "id": "src/components/ace-header/ace-header.types.ts::IAceHeaderTranslations"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "translations",
                "reflect": false
            },
            "offcanvasMenuOpen": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "offcanvas-menu-open",
                "reflect": true,
                "defaultValue": "false"
            },
            "searchTerms": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false,
                "attribute": "search-terms",
                "reflect": true
            },
            "config": {
                "type": "unknown",
                "mutable": false,
                "complexType": {
                    "original": "IAceHeaderConfig",
                    "resolved": "IAceHeaderConfig",
                    "references": {
                        "IAceHeaderConfig": {
                            "location": "import",
                            "path": "./ace-header.types",
                            "id": "src/components/ace-header/ace-header.types.ts::IAceHeaderConfig"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "getter": false,
                "setter": false
            }
        };
    }
    static get states() {
        return {
            "sportsFilterOpen": {}
        };
    }
    static get events() {
        return [{
                "method": "balanceButtonClicked",
                "name": "balanceButtonClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "registerButtonClicked",
                "name": "registerButtonClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "loginButtonClicked",
                "name": "loginButtonClicked",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "offcanvasMenuOpened",
                "name": "offcanvasMenuOpened",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "offcanvasMenuClosed",
                "name": "offcanvasMenuClosed",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "searchOpened",
                "name": "searchOpened",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "searchClosed",
                "name": "searchClosed",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "searchTermsChanged",
                "name": "searchTermsChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "searchEnterPressed",
                "name": "searchEnterPressed",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "searchFocusOut",
                "name": "searchFocusOut",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "headerHeightChanged",
                "name": "headerHeightChanged",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": ""
                },
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                }
            }];
    }
    static get elementRef() { return "hostElement"; }
    static get watchers() {
        return [{
                "propName": "userInfo",
                "methodName": "parseUserInfo"
            }, {
                "propName": "translations",
                "methodName": "parseTranslations"
            }, {
                "propName": "config",
                "methodName": "parseConfig"
            }];
    }
    static get listeners() {
        return [{
                "name": "searchOverlayClicked",
                "method": "handleSearchOverlayClicked",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "overlayClicked",
                "method": "overlayClickedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "keydown",
                "method": "handleKeyDown",
                "target": undefined,
                "capture": false,
                "passive": false
            }, {
                "name": "sportsFilterOpened",
                "method": "handleSportsFilterOpened",
                "target": "document",
                "capture": false,
                "passive": false
            }, {
                "name": "sportsFilterClosed",
                "method": "handleSportsFilterClosed",
                "target": "document",
                "capture": false,
                "passive": false
            }, {
                "name": "offcanvasMenuClosed",
                "method": "offcanvasMenuClosedHandler",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=ace-header.js.map
